import {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
  forwardRef,
} from "react";
import { useNavigate } from "react-router";
import { useUser } from "@supabase/auth-helpers-react";
import { useTranslation } from "react-i18next";
import { Tags } from "../tutorial/TutorialTags";
import { TutorialActions } from "./TutorialActions";
import { TutorialMetadata } from "./TutorialMetadata";
import { TutorialThumbnail } from "./TutorialThumbnail";
import { TutorialOverviewHeader } from "./TutorialOverviewHeader";
import { useSavedTutorials } from "@/hooks/useSavedTutorials";
import { useUninterestedTutorials } from "@/hooks/useUninterestedTutorials";
import { cn } from "@/lib/utils";
import { formatDistanceToNowLocalized } from "@/lib/date";
import { Tutorial } from "@/types/tutorial";
import {
  IMTutorialView,
  MixpanelActions,
} from "@/integrations/mixpanel/mixpanel";
import { useCategoryStore } from "@/stores/use-tutorial-progress";
import { useSubscription } from "@/contexts/SubscriptionContext";

interface TutorialOverviewProps {
  tutorial: Tutorial;
  rating: number;
  completedCount: number;
}

const TutorialOverview = forwardRef<HTMLDivElement, TutorialOverviewProps>(
  ({ tutorial, rating, completedCount }, ref) => {
    const user = useUser();
    const subscription = useSubscription();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const { updateTutorialPref } = useCategoryStore();
    const { isTutorialSaved, toggleSavedTutorial } = useSavedTutorials(
      user?.id
    );
    const { isUninterested, toggleUninterested } = useUninterestedTutorials(
      user?.id
    );
    const startTimeRef = useRef(null);

    const toolNames = useMemo(
      () => tutorial.tools.map((tool) => tool.name),
      [tutorial.tools]
    );

    const updatedTimeAgo = useMemo(
      () =>
        formatDistanceToNowLocalized(
          tutorial.updatedAt ?? new Date(),
          i18n.language
        ),
      [tutorial.updatedAt, i18n.language]
    );

    // We'll let CSS handle the responsive behavior instead of JavaScript
    const mxPanelTutorial: IMTutorialView = useMemo(
      () => ({
        tutorial_id: tutorial.id,
        tutorial_title: tutorial.title,
        author: tutorial.creator.name,
        tags: tutorial.tags,
        duration: tutorial.duration,
      }),
      [
        tutorial.id,
        tutorial.title,
        tutorial.creator.name,
        tutorial.tags,
        tutorial.duration,
      ]
    );

    useEffect(() => {
      startTimeRef.current = Date.now();

      return () => {
        if (startTimeRef.current) {
          const durationMs = Date.now() - startTimeRef.current;
          const durationSeconds = Math.round(durationMs / 1000);

          // MixpanelActions.tutorialTimeSpend(mxPanelTutorial, durationSeconds);
          startTimeRef.current = null;
        }
      };
    }, []);

    const handleSaveToggle = useCallback(
      async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user) return;
        MixpanelActions.tutorialSaved(mxPanelTutorial);
        toggleSavedTutorial(tutorial.documentId);
      },
      [user, tutorial.documentId, toggleSavedTutorial, mxPanelTutorial]
    );

    const handleNotInterested = useCallback(
      async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user?.id) return;
        MixpanelActions.tutorialDismissed(mxPanelTutorial);
        toggleUninterested(tutorial.documentId);
        updateTutorialPref(tutorial.documentId);
      },
      [user?.id, tutorial.documentId, toggleUninterested, mxPanelTutorial]
    );

    const isBlocked = false;
    
    const handleClick = useCallback(() => {
      if (!tutorial.documentId) {
        console.error("No documentId provided for tutorial:", tutorial.id);
        return;
      }
      if (isBlocked) {
        return;
      }
      sessionStorage.setItem("lastTutorialId", tutorial.documentId);
      navigate(`/tutorial/${encodeURIComponent(tutorial.documentId)}`);
    }, [navigate, tutorial.id, tutorial.documentId]);
    return (
      <div
        ref={ref}
        onClick={handleClick}
        role="article"
        tabIndex={0}
        className={cn(
          "group relative rounded-xl border shadow-sm overflow-hidden",
          "transition-all duration-300 hover:shadow-lg animate-fade-in",
          "bg-background border-border mx-auto lg:mx-0 lg:w-auto w-[97%] px-2 lg:px-0",
          {
            "hover:cursor-not-allowed":
              isBlocked,
            "hover:cursor-auto": !isBlocked,
          }
        )}
      >
        <TutorialThumbnail
          src={tutorial.thumbnailUrl}
          alt={tutorial.title}
          muted={isBlocked}
        />

        <div
          className={cn("p-4", {
            "hover:cursor-not-allowed":
              isBlocked,
          })}
        >
          <div className="flex flex-col space-y-2">
            <TutorialOverviewHeader
              title={tutorial.title}
              description={tutorial.description}
              creator={tutorial.creator}
              muted={isBlocked}
            />

            <TutorialMetadata
              creatorName={tutorial.creator?.name ?? "Anonymous"}
              updatedTimeAgo={updatedTimeAgo}
              completedCount={completedCount}
              duration={tutorial.duration ?? ""}
              rating={rating}
            />
          </div>

          <div className="flex flex-col xl:flex-row xl:items-center gap-4 mt-4">
            <div className="min-w-0 flex-1 overflow-visible">
              <Tags tags={[...(tutorial.tags ?? []), ...toolNames]} />
            </div>
            <div className="flex-shrink-0">
              <TutorialActions
                id={tutorial.id}
                saved={isTutorialSaved(tutorial.documentId)}
                onSave={handleSaveToggle}
                onNotInterested={handleNotInterested}
                isUninterested={isUninterested(tutorial.documentId)}
                muted={isBlocked}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default TutorialOverview;
